'use client';

import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@myapp/ui/components/dialog';
import { Button } from '@myapp/ui/components/button';
import { Badge } from '@myapp/ui/components/badge';

interface PurchaseSuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderInfo?: {
    orderId: string;
    productName: string;
    quantity: string;
    totalPrice: string;
    deliveryDate: string;
  };
}

export function PurchaseSuccessDialog({
  isOpen,
  onClose,
  orderInfo = {
    orderId: 'ORD-2024091101',
    productName: '강원도 감자',
    quantity: '5kg',
    totalPrice: '15,000원',
    deliveryDate: '9월 13일(금)'
  }
}: PurchaseSuccessDialogProps) {
  
  const handleContinueShopping = () => {
    onClose();
    // Navigate to seasonal page
    window.location.href = '/seasonal';
  };

  const handleViewOrders = () => {
    onClose();
    // Navigate to orders page
    console.log('주문내역 보기');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <DialogTitle className="text-2xl font-bold text-green-800">
            주문이 완료되었습니다!
          </DialogTitle>
          <p className="text-gray-600 mt-2">
            신선한 강원도 특산물이 곧 배송됩니다
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Package className="w-4 h-4" />
              주문번호: <span className="font-medium text-gray-900">{orderInfo.orderId}</span>
            </div>
            
            <div className="border-t border-gray-200 pt-3">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">{orderInfo.productName}</h4>
                  <p className="text-sm text-gray-600">{orderInfo.quantity}</p>
                </div>
                <p className="font-bold text-lg text-green-600">{orderInfo.totalPrice}</p>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  배송예정
                </Badge>
                <span className="text-sm text-gray-600">{orderInfo.deliveryDate} 도착예정</span>
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">배송 안내</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• 농가에서 직접 포장하여 신선도를 보장합니다</li>
              <li>• 배송 전 SMS로 알림을 보내드려요</li>
              <li>• 상품 문의는 고객센터로 연락해 주세요</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleViewOrders}
              className="flex-1"
            >
              주문내역 보기
            </Button>
            <Button
              onClick={handleContinueShopping}
              className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
            >
              계속 쇼핑하기
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Additional Actions */}
          <div className="text-center pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-3">
              이 상품과 어울리는 레시피를 찾아보세요
            </p>
            <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700">
              AI 레시피 추천 받기 →
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}